/**
 * Financial Weather Component
 * 
 * A reusable weather-style indicator showing cashflow health.
 * Used on Dashboard, Cashflow page, and Weekly Close.
 * 
 * Status Levels:
 * - ☀️ Calm: Balance comfortably above zero
 * - 🌤️ Stable: Balance positive but dips lower
 * - ⚠️ Tight Week: Balance approaches zero before next income
 * - 🌧️ Cashflow Risk: Projected balance goes negative
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Sun, 
  CloudSun, 
  Cloud, 
  CloudRain,
  ChevronRight,
  PiggyBank,
  Sliders,
  Calendar,
  FileText,
  ArrowRight,
  Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format currency
function formatCurrency(amount, currency = 'NZD') {
  if (amount === null || amount === undefined) return '$0';
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(amount || 0);
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Weather icon component
function WeatherIcon({ status, size = 'md' }) {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };
  
  const iconClass = sizeClasses[size] || sizeClasses.md;
  
  const icons = {
    calm: <Sun className={`${iconClass} text-emerald-500`} />,
    stable: <CloudSun className={`${iconClass} text-sky-500`} />,
    tight_week: <Cloud className={`${iconClass} text-amber-500`} />,
    cashflow_risk: <CloudRain className={`${iconClass} text-rose-500`} />
  };
  
  return icons[status] || icons.calm;
}

// Compact Weather Badge (for Dashboard tiles)
export function WeatherBadge({ weather, onClick }) {
  if (!weather) return null;
  
  const bgColors = {
    calm: 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800',
    stable: 'bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800',
    tight_week: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
    cashflow_risk: 'bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800'
  };
  
  const textColors = {
    calm: 'text-emerald-700 dark:text-emerald-400',
    stable: 'text-sky-700 dark:text-sky-400',
    tight_week: 'text-amber-700 dark:text-amber-400',
    cashflow_risk: 'text-rose-700 dark:text-rose-400'
  };
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-transform hover:scale-105 ${bgColors[weather.status]} ${textColors[weather.status]}`}
      data-testid="weather-badge"
    >
      <span>{weather.emoji}</span>
      <span>{weather.label}</span>
    </button>
  );
}

// Full Weather Card (for Cashflow page and Dashboard expanded view)
export function WeatherCard({ weather, currency, showActions = true, onActionClick, compact = false }) {
  const navigate = useNavigate();
  
  if (!weather) return null;
  
  const bgColors = {
    calm: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    stable: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800',
    tight_week: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    cashflow_risk: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800'
  };
  
  const textColors = {
    calm: 'text-emerald-700 dark:text-emerald-400',
    stable: 'text-sky-700 dark:text-sky-400',
    tight_week: 'text-amber-700 dark:text-amber-400',
    cashflow_risk: 'text-rose-700 dark:text-rose-400'
  };
  
  const actionIcons = {
    'move_funds': PiggyBank,
    'adjust_allocation': Sliders,
    'delay_spending': Calendar,
    'review_bills': FileText
  };
  
  const handleAction = (path) => {
    if (onActionClick) {
      onActionClick(path);
    } else if (path) {
      navigate(path);
    }
  };
  
  return (
    <div 
      className={`rounded-xl border-2 ${compact ? 'p-4' : 'p-5'} ${bgColors[weather.status]}`}
      data-testid="weather-card"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${bgColors[weather.status]}`}>
          <WeatherIcon status={weather.status} size={compact ? 'sm' : 'md'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">{weather.emoji}</span>
            <h3 className={`font-heading font-semibold ${textColors[weather.status]}`}>
              {weather.label}
            </h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {weather.explanation}
          </p>
        </div>
        
        {/* Next Income */}
        {weather.next_income_date && !compact && (
          <div className="text-right hidden sm:block">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Next money in</p>
            <p className="font-medium text-[#1a4d3e]">
              {formatCurrency(weather.next_income_amount, currency)}
            </p>
            <p className="text-xs text-slate-500">
              {formatDate(weather.next_income_date)}
            </p>
          </div>
        )}
      </div>
      
      {/* Suggested Actions */}
      {showActions && weather.suggested_actions?.length > 0 && (
        <div className={`border-t border-slate-200/50 dark:border-slate-700/50 ${compact ? 'pt-3 mt-3' : 'pt-4 mt-4'}`}>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">What you can do</p>
          <div className="space-y-2">
            {weather.suggested_actions.slice(0, compact ? 2 : 4).map((action, i) => {
              const ActionIcon = actionIcons[action.type] || ArrowRight;
              return (
                <button
                  key={i}
                  onClick={() => handleAction(action.action_path)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors ${
                    action.action_path 
                      ? 'hover:bg-white/50 dark:hover:bg-slate-800/50 cursor-pointer' 
                      : 'cursor-default'
                  }`}
                  data-testid={`weather-action-${action.type}`}
                >
                  <div className="p-1.5 rounded-lg bg-white dark:bg-slate-800">
                    <ActionIcon className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-700 dark:text-slate-300">
                      {action.title}
                    </p>
                    {!compact && (
                      <p className="text-xs text-slate-500 truncate">
                        {action.description}
                        {action.context?.total_savings_available > 0 && (
                          <span className="text-[#1a4d3e]">
                            {' '}• {formatCurrency(action.context.total_savings_available, currency)} available
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  {action.action_path && (
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Weekly Close Weather Summary
export function WeatherSummary({ weather, currency }) {
  if (!weather) return null;
  
  const bgColors = {
    calm: 'bg-emerald-50 dark:bg-emerald-900/20',
    stable: 'bg-sky-50 dark:bg-sky-900/20',
    tight_week: 'bg-amber-50 dark:bg-amber-900/20',
    cashflow_risk: 'bg-rose-50 dark:bg-rose-900/20'
  };
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${bgColors[weather.status]}`} data-testid="weather-summary">
      <span className="text-2xl">{weather.emoji}</span>
      <div>
        <p className="font-medium text-slate-900 dark:text-slate-100">{weather.label}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">{weather.explanation}</p>
      </div>
    </div>
  );
}

// Hook to fetch Financial Weather
export function useFinancialWeather(householdId) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!householdId) {
      setLoading(false);
      return;
    }
    
    const fetchWeather = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/households/${householdId}/cashflow/weather`,
          { credentials: 'include' }
        );
        
        if (res.ok) {
          const data = await res.json();
          setWeather(data);
          setError(null);
        } else {
          setError('Failed to load weather');
        }
      } catch (e) {
        console.error('Failed to fetch financial weather:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchWeather();
  }, [householdId]);
  
  return { weather, loading, error };
}

// Loading placeholder
export function WeatherLoading({ compact = false }) {
  return (
    <div className={`flex items-center justify-center ${compact ? 'py-3' : 'py-6'}`}>
      <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
    </div>
  );
}

// Default export - full card
export default WeatherCard;
